"use client";

import { MAIN_SITE_ROUTES } from "@frontend/roz-sdk-core";
import {
  UiFooter,
  UiFooterBottom,
  UiFooterSocialEnum,
  UiFooterTop,
} from "@frontend/roz-uikit";
import { createPortal } from "react-dom";

import { cn } from "@/lib/cn";
import { NavLink } from "@/primitives";
import { useSdkConfig } from "@/provider";
import type { FooterConfig, FooterSlots } from "@/types";

export interface StandardFooterProps extends FooterConfig {
  className?: string;
  slots?: FooterSlots;
}

const MAIN_PHONE = "+7 (499) 393-33-38";
const HOTLINE_PHONE = "8 (800) 550-22-02";

export const StandardFooter = ({
  className,
  containerId,
  slots,
}: StandardFooterProps) => {
  const { _usage } = useSdkConfig();
  // Внутренние приложения (front/cms, _usage: "internal") рендерят футер
  // инлайн — он липнет к низу flex-колонки через mt-auto. Портируем в body
  // только для внешнего UMD-встраивания (_usage: "external"), где шелл
  // монтируется в #roz-sdk-root наверху body, либо в явный containerId.
  const target =
    typeof document === "undefined"
      ? null
      : containerId
        ? document.getElementById(containerId)
        : _usage === "external"
          ? document.body
          : null;

  const footer = (
    <UiFooter
      as="footer"
      className={cn("sdkui:mt-auto", className)}
      id="roz-footer"
      link={NavLink}
    >
      {slots?.top}
      <UiFooterTop
        accentLinks={[
          {
            icon: "lector",
            label: "Стать лектором",
            href: "https://znan.ru/lektor_vneshnyaya_zayavka",
            target: "_blank",
            rel: "noreferrer nofollow",
            "data-testid": "footer_accent_become_lector",
          },
          {
            href: MAIN_SITE_ROUTES.becomeMember,
            icon: "member",
            label: "Стать Членом РОЗ",
            "data-testid": "footer_accent_become_member",
          },
          {
            href: MAIN_SITE_ROUTES.becomePartner,
            icon: "partner",
            label: "Стать партнером РОЗ",
            "data-testid": "footer_accent_become_partner",
          },
          {
            href: "https://forms.znanierussia.ru/forms/310fef0c-6f8e-4fde-966b-1a6f253d5d3e",
            icon: "feather",
            target: "_blank",
            label: "Стать автором",
            "data-testid": "footer_accent_become_author",
          },
          {
            href: "https://forms.znanierussia.ru/forms/0b8cb218-db0e-4e53-80c0-d6a6887d28d0",
            icon: "star",
            target: "_blank",
            label: "Стать экспертом",
            "data-testid": "footer_accent_become_expert",
          },
          {
            href: "https://znan.ru/suspicious_lecture",
            icon: "alert",
            label: "Сообщить о подозрительной лекции",
            target: "_blank",
            rel: "noreferrer nofollow",
            "data-testid": "footer_accent_report_lecture",
          },
        ]}
        contacts={{
          phones: [MAIN_PHONE, HOTLINE_PHONE],
          other: [
            {
              label: "Техническая поддержка:",
              type: "link",
              value: {
                href: "mailto:support@znanierussia.ru",
                label: "support@znanierussia.ru",
              },
            },
            {
              label: "Приемная Общества «Знание»:",
              type: "link",
              value: {
                href: "mailto:info@znanierussia.ru",
                label: "info@znanierussia.ru",
              },
            },
            {
              label: "Получение прав на фильмы Российского общества «Знание»:",
              type: "link",
              value: {
                href: "mailto:license@znanierussia.ru",
                label: "license@znanierussia.ru",
              },
            },
            {
              label: "Адрес организации:",
              type: "text",
              value:
                "109240, Москва, Внутригородская территория муниципальный округ Таганский, Николоямская д 11 строение 1",
            },
          ],
        }}
        links={{
          col1: [
            {
              href: MAIN_SITE_ROUTES.events,
              label: "Календарь мероприятий",
              "data-testid": "footer_link_events",
            },
            {
              href: MAIN_SITE_ROUTES.projects,
              label: "Проекты",
              "data-testid": "footer_link_projects",
            },
            {
              href: MAIN_SITE_ROUTES.cinema,
              label: "Кинотеатр",
              "data-testid": "footer_link_cinema",
            },
            {
              href: MAIN_SITE_ROUTES.library,
              label: "Лекции",
              "data-testid": "footer_link_library",
            },
            {
              href: "https://znanierussia.ru/articles",
              label: "Статьи",
              "data-testid": "footer_link_articles",
            },
            {
              href: "https://akademiya.znanierussia.ru/",
              label: "Курсы",
              "data-testid": "footer_link_courses",
            },
            // TODO: подкасты. Скрыть кнопку. Удалить, если будет сформирована задача на полное вырезание функционала
            // {
            //   href: MAIN_SITE_ROUTES.podcasts,
            //   label: "Подкасты",
            //   "data-testid": "footer_link_podcasts",
            // },
            {
              href: MAIN_SITE_ROUTES.oVajnom,
              label: "Разговоры о важном",
              "data-testid": "footer_link_razgovory_o_vazhnom",
            },
          ],
          col2: [
            {
              href: MAIN_SITE_ROUTES.speakers,
              label: "Наши Лекторы",
              "data-testid": "Our-lecture",
            },
            {
              href: MAIN_SITE_ROUTES.about,
              label: "Об обществе",
              "data-testid": "footerLinkAbout",
            },
            {
              href: MAIN_SITE_ROUTES.management,
              label: "Руководство",
              "data-testid": "footer_link_managment",
            },
            {
              href: MAIN_SITE_ROUTES.news,
              label: "Новости",
              "data-testid": "footer_link_news",
            },
            {
              href: MAIN_SITE_ROUTES.press,
              label: "Прессе",
              "data-testid": "footer_link_press",
            },
            {
              href: "https://nagrady.znanierussia.ru/",
              label: "Награды Российского общества Знание",
              "data-testid": "footer_link_awards",
            },
          ],
        }}
        socials={[
          {
            icon: UiFooterSocialEnum.vk,
            link: "https://vk.com/znanierussia",
            "data-testid": "vk",
          },
          {
            icon: UiFooterSocialEnum.tg,
            link: "https://t.me/Znanie_Russia",
            "data-testid": "telegram",
          },
          {
            icon: UiFooterSocialEnum.max,
            link: "https://max.ru/ZnanieRussia",
            "data-testid": "max",
          },
        ]}
      />
      <UiFooterBottom
        col2={[
          {
            href: "https://roz-events.storage.yandexcloud.net/media/documents/privacy_policy.pdf",
            label: "Политика конфиденциальности",
            rel: "noreferrer nofollow",
            target: "_blank",
            "data-testid": "footer_bottom_privacy_policy",
          },
          {
            href: "https://znan.ru/polzovatelskoe-soglashenie-ob-ispolzovanii-sajta",
            label: "Пользовательское соглашение",
            rel: "noreferrer nofollow",
            target: "_blank",
            "data-testid": "footer_bottom_user_agreement",
          },
          {
            href: "https://znanierussia.ru/versii-dokumentov-soglasiya-na-obrabotku-personalnyh-dannyh",
            label: "Согласие на обработку персональных данных",
            rel: "noreferrer nofollow",
            target: "_blank",
            "data-testid": "footer_bottom_personal_data_consent",
          },
          {
            href: "https://znan.ru/politobrpd",
            label: "Политика в отношении обработки персональных данных",
            rel: "noreferrer nofollow",
            target: "_blank",
            "data-testid": "footer_bottom_personal_data_policy",
          },
          {
            href: "https://www.consultant.ru/document/cons_doc_LAW_61801/",
            label: "ФЗ РФ №152-ФЗ «О персональных данных»",
            rel: "noreferrer nofollow",
            target: "_blank",
            "data-testid": "footer_bottom_law_152",
          },
          {
            href: "http://ivo.garant.ru/#/document/12148555/paragraph/3471:0",
            label: "ФЗ РФ №149-ФЗ «О защите информации»",
            rel: "noreferrer nofollow",
            target: "_blank",
            "data-testid": "footer_bottom_law_149",
          },
        ]}
        col3={[
          {
            href: "https://znan.ru/ustav-roz",
            label: "Устав Российского общества «Знание»",
            rel: "noreferrer nofollow",
            target: "_blank",
            "data-testid": "footer_bottom_charter",
          },
          {
            href: "https://roz-events.storage.yandexcloud.net/media/documents/%D0%A3%D0%BA%D0%B0%D0%B7_%D0%9F%D1%80%D0%B5%D0%B7%D0%B8%D0%B4%D0%B5%D0%BD%D1%82%D0%B0_%D0%A0%D0%A4_%D0%BE%D1%82_11.12.2015_617.pdf",
            label: "Указ о создании Российского общества «Знание»",
            rel: "noreferrer nofollow",
            target: "_blank",
            "data-testid": "footer_bottom_decree",
          },
        ]}
      />
    </UiFooter>
  );

  return target ? createPortal(footer, target) : footer;
};
